import React, { useState } from "react";
import {
  X,
  User,
  CurrencyDollar,
  CreditCard,
  Coins,
  Plus,
  Minus,
  Trash,
  Calendar,
  Bank,
  ChartLine,
  Wallet,
  Building,
  House,
  Car,
  ForkKnife,
  Lightning,
  FirstAid,
  Shield,
  Receipt,
  GameController,
  UserCircle,
  Briefcase,
  GraduationCap,
  PiggyBank,
  TrendUp,
  Airplane,
  Lock,
  Check,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { User as UserType, Asset, Debt } from "../types";
import { formatCurrency } from "../utils/calculations";
import DollarInput from "./DollarInput";

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface IncomeSource {
  id: string;
  name: string;
  amount: number;
  frequency: "monthly" | "weekly" | "biweekly" | "yearly";
}

interface ExpenseEstimate {
  category: string;
  amount: number;
}

interface AccountItem {
  id: string;
  name: string;
  type:
    | "401k"
    | "brokerage"
    | "hysa"
    | "checking"
    | "savings"
    | "ira"
    | "other";
  balance: number;
  monthlyContribution?: number;
  interestRate?: number;
}

interface CreditCardItem {
  id: string;
  name: string;
  balance: number;
  limit: number;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { dispatch, getCurrentUser } = useData();
  const [step, setStep] = useState(1);

  // Function to scroll to top when step changes
  const scrollToTop = () => {
    const modalContent = document.querySelector(".onboarding-modal-content");
    if (modalContent) {
      modalContent.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Scroll to top when step changes
  React.useEffect(() => {
    scrollToTop();
  }, [step]);

  // Account creation
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [currentAge, setCurrentAge] = useState("");
  const [email, setEmail] = useState("");

  // Financial data
  const [incomeSources, setIncomeSources] = useState<IncomeSource[]>([
    { id: "1", name: "Salary", amount: 0, frequency: "monthly" },
  ]);
  const [expenseEstimates, setExpenseEstimates] = useState<ExpenseEstimate[]>([
    { category: "Housing", amount: 0 },
    { category: "Groceries", amount: 0 },
    { category: "Transportation", amount: 0 },
    { category: "Utilities", amount: 0 },
    { category: "Entertainment", amount: 0 },
    { category: "Medical", amount: 0 },
  ]);
  const [accounts, setAccounts] = useState<AccountItem[]>([
    {
      id: "1",
      name: "401(k)",
      type: "401k",
      balance: 0,
      monthlyContribution: 0,
    },
    {
      id: "2",
      name: "High-Yield Savings",
      type: "hysa",
      balance: 0,
      interestRate: 4.5,
    },
    { id: "3", name: "Brokerage Account", type: "brokerage", balance: 0 },
  ]);

  const [creditCards, setCreditCards] = useState<CreditCardItem[]>([
    { id: "1", name: "Chase Freedom", balance: 0, limit: 0 },
  ]);

  const addIncomeSource = () => {
    const newId = `income-${Date.now()}`;
    const newSource: IncomeSource = {
      id: newId,
      name: "",
      amount: 0,
      frequency: "monthly",
    };
    console.log("Before adding:", incomeSources.length);
    setIncomeSources([...incomeSources, newSource]);
    console.log("After adding:", incomeSources.length + 1);
  };

  const updateIncomeSource = (
    id: string,
    field: keyof IncomeSource,
    value: any
  ) => {
    setIncomeSources(
      incomeSources.map((source) =>
        source.id === id ? { ...source, [field]: value } : source
      )
    );
  };

  const removeIncomeSource = (id: string) => {
    if (incomeSources.length > 1) {
      setIncomeSources(incomeSources.filter((source) => source.id !== id));
    }
  };

  const updateExpenseEstimate = (category: string, amount: number) => {
    setExpenseEstimates(
      expenseEstimates.map((expense) =>
        expense.category === category ? { ...expense, amount } : expense
      )
    );
  };

  const addAccount = () => {
    const newId = (accounts.length + 1).toString();
    setAccounts([
      ...accounts,
      { id: newId, name: "", type: "other", balance: 0 },
    ]);
  };

  const updateAccount = (id: string, field: keyof AccountItem, value: any) => {
    setAccounts(
      accounts.map((account) =>
        account.id === id ? { ...account, [field]: value } : account
      )
    );
  };

  const removeAccount = (id: string) => {
    if (accounts.length > 1) {
      setAccounts(accounts.filter((account) => account.id !== id));
    }
  };

  const addCreditCard = () => {
    const newId = (creditCards.length + 1).toString();
    setCreditCards([
      ...creditCards,
      { id: newId, name: "", balance: 0, limit: 0 },
    ]);
  };

  const updateCreditCard = (
    id: string,
    field: keyof CreditCardItem,
    value: any
  ) => {
    setCreditCards(
      creditCards.map((card) =>
        card.id === id ? { ...card, [field]: value } : card
      )
    );
  };

  const removeCreditCard = (id: string) => {
    if (creditCards.length > 1) {
      setCreditCards(creditCards.filter((card) => card.id !== id));
    }
  };

  const handleComplete = () => {
    // Create user with username, age, and email (email is optional)
    const newUser: UserType = {
      id: "1",
      name: username,
      email: email || "", // Email is optional, can be added later
      avatar: username.charAt(0).toUpperCase(),
      age: currentAge ? parseInt(currentAge) : undefined,
      createdAt: new Date().toISOString(),
    };

    // Add user and set as current user
    dispatch({ type: "ADD_USER", payload: newUser });
    dispatch({ type: "SET_CURRENT_USER", payload: "1" });

    // Create income sources
    const now = new Date();
    incomeSources.forEach((source) => {
      if (source.name && source.amount > 0) {
        const incomeSource = {
          id: `income-source-${source.id}`,
          userId: "1",
          name: source.name,
          category: source.name,
          amount: source.amount,
          frequency: source.frequency,
          description: `${source.name} - ${source.frequency}`,
          isActive: true,
          createdAt: now.toISOString(),
        };
        dispatch({ type: "ADD_INCOME_SOURCE", payload: incomeSource });

        const transaction = {
          id: `income-${source.id}`,
          userId: "1",
          type: "income" as const,
          category: source.name,
          amount: source.amount,
          description: `${source.name} - ${source.frequency}`,
          date: now.toISOString().split("T")[0],
          createdAt: now.toISOString(),
        };
        dispatch({ type: "ADD_TRANSACTION", payload: transaction });
      }
    });

    // Create transactions for expense estimates
    expenseEstimates.forEach((expense) => {
      if (expense.amount > 0) {
        const transaction = {
          id: `expense-${expense.category.toLowerCase()}`,
          userId: "1",
          type: "expense" as const,
          category: expense.category,
          amount: expense.amount,
          description: `Monthly ${expense.category} expense`,
          date: now.toISOString().split("T")[0],
          createdAt: now.toISOString(),
        };
        dispatch({ type: "ADD_TRANSACTION", payload: transaction });
      }
    });

    // Create assets from accounts
    accounts.forEach((account) => {
      if (account.name && account.balance > 0) {
        let assetType: "retirement" | "investment" | "savings" | "other";
        switch (account.type) {
          case "401k":
          case "ira":
            assetType = "retirement";
            break;
          case "brokerage":
            assetType = "investment";
            break;
          case "hysa":
          case "savings":
            assetType = "savings";
            break;
          default:
            assetType = "other";
        }

        const asset = {
          id: `asset-${account.id}`,
          userId: "1",
          name: account.name,
          type: assetType,
          balance: account.balance,
          monthlyContribution: account.monthlyContribution || 0,
          interestRate: account.interestRate || 0,
          isLiquid:
            account.type === "hysa" ||
            account.type === "checking" ||
            account.type === "savings",
          createdAt: now.toISOString(),
        };
        dispatch({ type: "ADD_ASSET", payload: asset });
      }
    });

    // Create credit cards
    creditCards.forEach((card) => {
      if (card.name && (card.balance > 0 || card.limit > 0)) {
        const debt = {
          id: `debt-${card.id}`,
          userId: "1",
          name: card.name,
          type: "credit-card" as Debt["type"],
          balance: card.balance,
          monthlyPayment: 0,
          interestRate: 0,
          isActive: true,
          createdAt: now.toISOString(),
        };
        dispatch({ type: "ADD_DEBT", payload: debt });
      }
    });

    onComplete();
  };

  const canProceedToStep2 =
    username.length >= 3 && pin.length === 4 && pin === confirmPin;
  const canProceedToStep3 = incomeSources.some((s) => s.name && s.amount > 0);
  const canProceedToStep4 = expenseEstimates.some((e) => e.amount > 0);
  const canComplete = accounts.some((a) => a.name && a.balance > 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[95vh] overflow-y-auto onboarding-modal-content">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Create Your Account
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
            <X className="w-6 h-6" weight="duotone" />
          </button>
        </div>

        {/* USWDS Stepper */}
        <nav className="usa-step-indicator" aria-label="progress">
          <ol className="usa-step-indicator__segments">
            {[
              {
                number: 1,
                label: "Account Setup",
                status:
                  step >= 1 ? "current" : step > 1 ? "complete" : "incomplete",
              },
              {
                number: 2,
                label: "Income Sources",
                status:
                  step >= 2 ? "current" : step > 2 ? "complete" : "incomplete",
              },
              {
                number: 3,
                label: "Monthly Expenses",
                status:
                  step >= 3 ? "current" : step > 3 ? "complete" : "incomplete",
              },
              {
                number: 4,
                label: "Financial Accounts",
                status:
                  step >= 4 ? "current" : step > 4 ? "complete" : "incomplete",
              },
            ].map((stepInfo, index) => (
              <li
                key={stepInfo.number}
                className={`usa-step-indicator__segment usa-step-indicator__segment--${stepInfo.status}`}>
                <span className="usa-step-indicator__segment-label">
                  <span className="usa-step-indicator__segment-label-text">
                    {stepInfo.label}
                  </span>
                  <span className="usa-step-indicator__segment-label-marker">
                    {stepInfo.status === "complete" ? (
                      <Check className="w-4 h-4" weight="bold" />
                    ) : (
                      stepInfo.number
                    )}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </nav>

        {/* Step 1: Account Setup */}
        {step === 1 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Step 1: Account Setup
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="input w-full pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PIN (4 digits) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    placeholder="Enter 4-digit PIN"
                    value={pin}
                    onChange={(e) =>
                      setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    className="input w-full pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm PIN <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    placeholder="Confirm 4-digit PIN"
                    value={confirmPin}
                    onChange={(e) =>
                      setConfirmPin(
                        e.target.value.replace(/\D/g, "").slice(0, 4)
                      )
                    }
                    className="input w-full pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Age (Optional)
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    placeholder="Enter your age"
                    value={currentAge}
                    onChange={(e) => setCurrentAge(e.target.value)}
                    className="input w-full pl-10"
                    min="18"
                    max="120"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email (Optional)
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input w-full pl-10"
                  />
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Add an email to receive notifications and secure your account.
                  You can add or change this later in your profile.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Income Sources */}
        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Step 2: Income Sources
            </h3>
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                Add Your Income Sources
              </h4>
              {incomeSources.map((source, index) => (
                <div key={source.id} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-medium text-gray-900 dark:text-white">
                      Income Source {index + 1}
                    </h5>
                    <button
                      type="button"
                      onClick={() => removeIncomeSource(source.id)}
                      className="btn-icon text-red-500 hover:text-red-600"
                      aria-label="Remove income source">
                      <Trash className="w-4 h-4" weight="duotone" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Source Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={source.name}
                        onChange={(e) =>
                          updateIncomeSource(source.id, "name", e.target.value)
                        }
                        className="input w-full"
                        placeholder="e.g., Salary, Freelance, Investment"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Amount <span className="text-red-500">*</span>
                      </label>
                      <DollarInput
                        value={source.amount}
                        onChange={(value) =>
                          updateIncomeSource(source.id, "amount", value)
                        }
                        placeholder="$0.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Frequency
                      </label>
                      <select
                        value={source.frequency}
                        onChange={(e) =>
                          updateIncomeSource(
                            source.id,
                            "frequency",
                            e.target.value as any
                          )
                        }
                        className="select w-full">
                        <option value="monthly">Monthly</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={source.name}
                        onChange={(e) =>
                          updateIncomeSource(source.id, "name", e.target.value)
                        }
                        className="select w-full">
                        <option value="Salary">Salary</option>
                        <option value="Freelance">Freelance</option>
                        <option value="Investment">Investment</option>
                        <option value="Business">Business</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={addIncomeSource}
                      className="btn-primary flex items-center space-x-2">
                      <Plus className="w-4 h-4" />
                      <span>Add Another Income Source</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Monthly Expenses */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Step 3: Monthly Expenses
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Add Your Monthly Expenses
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    const newId = (expenseEstimates.length + 1).toString();
                    setExpenseEstimates([
                      ...expenseEstimates,
                      { category: "", amount: 0 },
                    ]);
                  }}
                  className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Expense</span>
                </button>
              </div>
              {expenseEstimates.map((expense, index) => (
                <div key={index} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-medium text-gray-900 dark:text-white">
                      Expense {index + 1}
                    </h5>
                    <button
                      type="button"
                      onClick={() => {
                        if (expenseEstimates.length > 1) {
                          setExpenseEstimates(
                            expenseEstimates.filter((_, i) => i !== index)
                          );
                        }
                      }}
                      className="btn-icon text-red-500 hover:text-red-600">
                      <Trash className="w-4 h-4" weight="duotone" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={expense.category}
                        onChange={(e) =>
                          updateExpenseEstimate(
                            expense.category,
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="select w-full">
                        <option value="Housing">Housing</option>
                        <option value="Groceries">Groceries</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Medical">Medical</option>
                        <option value="Insurance">Insurance</option>
                        <option value="Subscriptions">Subscriptions</option>
                        <option value="Personal">Personal</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Amount
                      </label>
                      <DollarInput
                        value={expense.amount}
                        onChange={(value) =>
                          updateExpenseEstimate(expense.category, value)
                        }
                        placeholder="$0.00"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Financial Accounts & Credit Cards */}
        {step === 4 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Step 4: Financial Accounts & Credit Cards
            </h3>

            {/* Financial Accounts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Financial Accounts
                </h4>
                <button
                  type="button"
                  onClick={addAccount}
                  className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Account</span>
                </button>
              </div>
              {accounts.map((account, index) => (
                <div key={index} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-medium text-gray-900 dark:text-white">
                      Account {index + 1}
                    </h5>
                    <button
                      type="button"
                      onClick={() => removeAccount(account.id)}
                      className="btn-icon text-red-500 hover:text-red-600"
                      aria-label="Remove account">
                      <Trash className="w-4 h-4" weight="duotone" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={account.name}
                        onChange={(e) =>
                          updateAccount(account.id, "name", e.target.value)
                        }
                        className="input w-full"
                        placeholder="e.g., 401(k), Emergency Fund"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={account.type}
                        onChange={(e) =>
                          updateAccount(
                            account.id,
                            "type",
                            e.target.value as AccountItem["type"]
                          )
                        }
                        className="select w-full">
                        <option value="401k">401(k)</option>
                        <option value="ira">IRA</option>
                        <option value="hysa">High-Yield Savings</option>
                        <option value="checking">Checking Account</option>
                        <option value="brokerage">Brokerage Account</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Balance <span className="text-red-500">*</span>
                      </label>
                      <DollarInput
                        value={account.balance}
                        onChange={(value) =>
                          updateAccount(account.id, "balance", value)
                        }
                        placeholder="$0.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Monthly Contribution
                      </label>
                      <DollarInput
                        value={account.monthlyContribution || 0}
                        onChange={(value) =>
                          updateAccount(
                            account.id,
                            "monthlyContribution",
                            value
                          )
                        }
                        placeholder="$0.00"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Credit Cards */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Credit Cards
                </h4>
                <button
                  type="button"
                  onClick={addCreditCard}
                  className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Card</span>
                </button>
              </div>
              {creditCards.map((card, index) => (
                <div key={index} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-medium text-gray-900 dark:text-white">
                      Credit Card {index + 1}
                    </h5>
                    <button
                      type="button"
                      onClick={() => removeCreditCard(card.id)}
                      className="btn-icon text-red-500 hover:text-red-600"
                      aria-label="Remove credit card">
                      <Trash className="w-4 h-4" weight="duotone" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Card Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) =>
                          updateCreditCard(card.id, "name", e.target.value)
                        }
                        className="input w-full"
                        placeholder="Card nickname"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current Balance <span className="text-red-500">*</span>
                      </label>
                      <DollarInput
                        value={card.balance}
                        onChange={(value) =>
                          updateCreditCard(card.id, "balance", value)
                        }
                        placeholder="$0.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Credit Limit
                      </label>
                      <DollarInput
                        value={card.limit}
                        onChange={(value) =>
                          updateCreditCard(card.id, "limit", value)
                        }
                        placeholder="$0.00"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              step === 1
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}>
            Previous
          </button>
          <button
            type="button"
            onClick={step === 4 ? handleComplete : () => setStep(step + 1)}
            className="btn-primary">
            {step === 4 ? "Complete Setup" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
